import { useCallback, useEffect, useRef, useState } from 'react';
import { Affix } from 'antd';
import { Outlet, useMatch, useParams } from 'react-router-dom';
import { useCurrent } from '@/providers';
import {
  createSupplierTrackEvent,
  getSupplierInfo,
  getSupplierTrackMemberLevel,
} from '@/services';
import { isInIframe } from '@/utils';
import { Footer } from './footer';
import { Header } from './head';
import { SimpleSearch } from './search';
import styles from './style.module.scss';

export const SimpleLayout = () => {
  const [supplierInfo, setSupplierInfo] = useState<any>(null);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(true);
  const {
    isLogin,
    memberQuotaBaseInfo,
    memberQuotaBaseIsFetched,
  } = useCurrent();
  const supplierRouteMatch = useMatch('/supplier/:id');
  const { id } = useParams();
  const isMounted = useRef(true);
  const upgradeBannerRef = useRef<HTMLDivElement>(null);
  const exposedUpgradeSlotsRef = useRef(new Set<string>());
  const upgradeSlotId = memberQuotaBaseInfo?.caOpenListHit
    ? 'ca_supplierlist_tipbar'
    : 'adv_portalhome_tipbar';
  const memberLevel = getSupplierTrackMemberLevel(memberQuotaBaseInfo);
  const isSupplierDetailPage =
    window.location.pathname.includes('/supplier/detail/');
  const shouldShowUpgradeBanner =
    !!supplierRouteMatch &&
    supplierInfo?.isOwner === true &&
    !!memberQuotaBaseIsFetched &&
    !!isLogin &&
    memberQuotaBaseInfo?.memberTypeCode !== 'PREMIUM_PACKAGE' &&
    showUpgradeBanner && !(memberQuotaBaseInfo?.caOpenListHit && memberQuotaBaseInfo?.caMemberTypeCode === 'FADADA_CA_CERTIFIED');

  const fetchSupplierInfo = async () => {
    if (!id) return;
    try {
      const res = await getSupplierInfo(id);
      if (isMounted.current) {
        setSupplierInfo(res);
      }
    } catch (error) {
      if (isMounted.current) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchSupplierInfo();

    return () => {
      isMounted.current = false;
    };
  }, [id]);

  const trackUpgradeBanner = useCallback(
    (
      eventName: 'guide_expose' | 'guide_click',
      eventType: 'EXPOSE' | 'CLICK',
      elementName: string,
      properties: Record<string, unknown> = {},
    ) => {
      createSupplierTrackEvent({
        eventId: `evt_${crypto.randomUUID()}`,
        eventName,
        eventType,
        eventTime: Date.now(),
        slotId: upgradeSlotId,
        memberLevel,
        inOpenList: memberQuotaBaseInfo?.caOpenListHit === true,
        device: 'pc',
        pageName: 'portal_home',
        moduleName: 'tipbar',
        elementName,
        properties,
      }).catch(() => undefined);
    },
    [memberLevel, memberQuotaBaseInfo?.caOpenListHit, upgradeSlotId],
  );

  useEffect(() => {
    const banner = upgradeBannerRef.current;
    if (
      !banner ||
      !shouldShowUpgradeBanner ||
      exposedUpgradeSlotsRef.current.has(upgradeSlotId)
    ) {
      return;
    }

    let exposeTimer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) {
          if (!exposeTimer) {
            exposeTimer = setTimeout(() => {
              exposedUpgradeSlotsRef.current.add(upgradeSlotId);
              trackUpgradeBanner('guide_expose', 'EXPOSE', 'tipbar', {
                visiblePercent: Math.round(entry.intersectionRatio * 100),
                visibleDurationMs: 1000,
              });
              observer.disconnect();
            }, 1000);
          }
          return;
        }

        if (exposeTimer) {
          clearTimeout(exposeTimer);
          exposeTimer = undefined;
        }
      },
      { threshold: [0, 0.5, 1] },
    );

    observer.observe(banner);
    return () => {
      if (exposeTimer) clearTimeout(exposeTimer);
      observer.disconnect();
    };
  }, [shouldShowUpgradeBanner, trackUpgradeBanner, upgradeSlotId]);

  const handleUpgradeClick = () => {
    const isCaOpenList = memberQuotaBaseInfo?.caOpenListHit === true;
    trackUpgradeBanner('guide_click', 'CLICK', 'open_service', {
      buttonText: isCaOpenList
        ? '开通CA认证供应商'
        : '开通高级企采商',
    });
    window.open(
      isCaOpenList ? '/caIntroduction' : '/become-supplier',
      '_blank',
    );
  };

  return (
    <div className={styles.globalWrapper}>
      <Affix>
        <div>
          {shouldShowUpgradeBanner && (
            <div
              ref={upgradeBannerRef}
              className={`${styles.upgradeNotice} ${
                memberQuotaBaseInfo?.caOpenListHit
                  ? styles.caUpgradeNotice
                  : ''
              }`}
            >
              <div className={styles.upgradeNoticeContent}>
                <span className={styles.upgradeNoticeIcon}>i</span>
                <span>
                  {memberQuotaBaseInfo?.caOpenListHit
                    ? '贵司门户暂未展示身份标识。，开通后身份标签全链路展示。'
                    : '开通高级企采商，门户可展示合作数据与更多产品位，并优先推荐给采购商。'}
                </span>
                <button
                  type="button"
                  className={styles.upgradeNoticeButton}
                  onClick={handleUpgradeClick}
                >
                  {memberQuotaBaseInfo?.caOpenListHit
                    ? '开通CA认证供应商 ›'
                    : '开通高级企采商 ›'}
                </button>
              </div>
              <button
                type="button"
                aria-label="关闭"
                className={styles.upgradeNoticeClose}
                onClick={() => setShowUpgradeBanner(false)}
              >
                ×
              </button>
            </div>
          )}
          {!isInIframe() && <Header noTabs />}
          <SimpleSearch supplierInfo={supplierInfo} />
        </div>
      </Affix>
      <div className={styles.outLayoutWrapper}>
        {isSupplierDetailPage ? (
          <Outlet context={{ supplierInfo, fetchSupplierInfo }} />
        ) : (
          <div className={styles.layoutWrapper}>
            <div className={styles.layoutContent}>
              <Outlet context={{ supplierInfo, fetchSupplierInfo }} />
            </div>
          </div>
        )}
      </div>
      <div style={{ width: 1200, margin: '0 auto' }}>
        {!isInIframe() && <Footer />}
      </div>
    </div>
  );
};
